import { ListButton } from "@refinedev/antd";
import { useGetIdentity } from "@refinedev/core";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Card,
  Collapse,
  ConfigProvider,
  Descriptions,
  Empty,
  Flex,
  Modal,
  Progress,
  Result,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
  message,
  theme,
} from "antd";
import {
  LeftOutlined,
  DownloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { axiosInstance } from "@/utilities";
import {
  API_URL_PIM_ATTRIBUTES,
  API_URL_PIM_IMPORTS,
} from "@/services/XCONS_ENV";
import type { ImportReport, ImportRun } from "./types";
import {
  HistorialDelCatalogo,
  TARJETA_CON_ENCABEZADO,
} from "./HistorialDelCatalogo";

const { Text, Title } = Typography;

/** Cuántas importaciones trae la lista de una vez, y cuántas suma al llegar al final. */
const DE_A_CUANTAS = 20;

/** "Últimas importaciones" mide 500px fijos; la tabla ocupa lo que queda adentro. */
const ALTO_ULTIMAS = 500;
/**
 * El recuadro de la tabla de adentro: la tarjeta menos su encabezado (40), su relleno
 * (12 + 12) y sus bordes. La tabla de columnas usa el mismo, para que las dos midan igual.
 */
const ALTO_TABLA_FIJA = ALTO_ULTIMAS - 40 - 24 - 2;

/**
 * El alto que le queda a una tabla de encabezado fijo adentro de su contenedor, menos su
 * encabezado: es el `scroll.y` que la hace entrar justa. Se mide, en vez de restar a mano,
 * porque lo que la rodea (un ícono, un relleno) cambia de alto.
 */
const useAltoDisponible = (inicial: number) => {
  const [alto, setAlto] = useState(inicial);
  const observador = useRef<ResizeObserver | null>(null);
  const medir = useCallback((el: HTMLDivElement | null) => {
    observador.current?.disconnect();
    if (!el) return;
    observador.current = new ResizeObserver(([entrada]) =>
      setAlto(Math.max(120, Math.floor(entrada.contentRect.height) - 42)),
    );
    observador.current.observe(el);
  }, []);
  return [medir, alto] as const;
};

/**
 * Una tabla que llena su contenedor: el recuadro con su borde ocupa todo el alto, aunque
 * tenga dos filas, y lo único que scrollea son las filas. Sin cuentas: toda la cadena de
 * antd pasa a ser una columna flex y el cuerpo se queda con lo que sobra. El `scroll.y` sólo
 * hace falta para que antd deje el encabezado fijo; su alto máximo se anula acá.
 */
const CSS_ALTO_FIJO = `
.tabla-alto-fijo,
.tabla-alto-fijo .ant-table-wrapper,
.tabla-alto-fijo .ant-spin-nested-loading,
.tabla-alto-fijo .ant-spin-container,
.tabla-alto-fijo .ant-table,
.tabla-alto-fijo .ant-table-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.tabla-alto-fijo .ant-table-wrapper,
.tabla-alto-fijo .ant-spin-nested-loading,
.tabla-alto-fijo .ant-spin-container,
.tabla-alto-fijo .ant-table,
.tabla-alto-fijo .ant-table-container { flex: 1; }
.tabla-alto-fijo .ant-table-body { flex: 1; min-height: 0; max-height: none !important; }
/* **El borde de afuera lo pone un solo recuadro, no antd.** antd reparte el contorno entre
   el contenedor (izquierda y arriba) y las celdas (derecha y abajo): con la tabla estirada
   quedaba abierto, o doble contra la última fila. Acá el contenedor y las celdas del borde
   se quedan sin su línea de afuera, y la pone el recuadro. */
.tabla-alto-fijo {
  /* El borde entra en el 100% de alto: si no, se pasa 2px y la tarjeta lo corta. */
  box-sizing: border-box;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
}
.tabla-alto-fijo .ant-table,
.tabla-alto-fijo .ant-table-container,
.tabla-alto-fijo .ant-table-header,
.tabla-alto-fijo .ant-table-thead > tr > th {
  border-radius: 0 !important;
}
.tabla-alto-fijo .ant-table-container { border: none !important; }
.tabla-alto-fijo .ant-table-thead > tr > th:last-child,
.tabla-alto-fijo .ant-table-tbody > tr > td:last-child { border-inline-end: none !important; }
.tabla-alto-fijo .ant-table-tbody > tr:last-child > td { border-bottom: none; }
`;

/** El mismo encabezado de sección que la ficha de producto. */
const TEMA_SECCION = {
  components: { Collapse: { headerPadding: "9px 16px" } },
};

/** El resultado del popup, más compacto que el de antd: el ícono sigue grande, con menos aire. */
const TEMA_RESUMEN = {
  components: {
    Result: { iconFontSize: 48, titleFontSize: 20, paddingLG: 12 },
  },
};

/** El nombre con que se reconoce una corrida: su archivo, o el número si no lo trae. */
const nombreDeCorrida = (corrida: ImportRun) =>
  corrida.filename || `Importación #${corrida.id}`;

/**
 * Las columnas **reservadas**: las que no son atributos del EAV y tienen su propio destino
 * en el producto. Los nombres son los códigos que espera el backend, tal cual.
 */
const RESERVADAS: Array<{ value: string; label: string }> = [
  { value: "sku", label: "SKU" },
  // **Un alta arma el SKU como el form** (xcons-docs#425): `MARCA-SKU Fabricante`. La
  // planilla puede traer el SKU XCONS ya armado o la Marca y el SKU Fabricante por separado.
  { value: "sku_fabricante", label: "SKU Fabricante" },
  // `attribute_set`, no `attributeSet`: el valor que se elige acá **es** el código que el
  // backend busca entre sus columnas reservadas. Con el camelCase la columna no se
  // reconocía y terminaba en el EAV como un atributo inexistente, así que la fila fallaba
  // entera y el conjunto nunca se aplicaba.
  { value: "attribute_set", label: "Conjunto de atributos" },
  { value: "qty", label: "Stock" },
  { value: "categories", label: "Categorías" },
  { value: "media", label: "Imágenes (URLs)" },
  { value: "attachments", label: "Fichas técnicas (URLs)" },
  { value: "related", label: "Productos relacionados (SKUs)" },
  { value: "crosssell", label: "Venta cruzada (SKUs)" },
  { value: "upsell", label: "Venta superior (SKUs)" },
  // El cuarto vínculo (xcons-docs#476): lo que se compra junto, no una alternativa.
  { value: "complementary", label: "Productos complementarios (SKUs)" },
];

/**
 * Los stores cuyo árbol de categorías administra quien importa: `permissions.store.categorias`,
 * igual que el ítem "Categorías" del menú del store. El API lo vuelve a chequear por fila.
 */
const storesConCategorias = (identity: any): string[] =>
  Object.entries(identity?.permissions?.store?.categorias ?? {})
    .filter(([, nivel]) => {
      const n = String(nivel ?? "")
        .trim()
        .toLowerCase();
      return n !== "" && n !== "no_access";
    })
    .map(([code]) => code)
    .sort();

/**
 * Un encabezado comparable, con la misma regla que el API (`normalizarEncabezado`): sin
 * mayúsculas, sin acentos, sin asteriscos y con los espacios colapsados.
 */
const normalizarEncabezado = (texto: string): string =>
  String(texto ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\*/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const ETIQUETA_FILA: Record<string, { label: string; color?: string }> = {
  draft: { label: "Borrador", color: "default" },
  pending: { label: "Versión pendiente", color: "gold" },
  direct: { label: "Escrito directo", color: "green" },
  failed: { label: "Con error", color: "red" },
  // Un existente al que la fila no le cambiaba nada (xcons-docs#468): no abrió versión.
  unchanged: { label: "Sin cambios", color: "default" },
};

/**
 * Importador de productos del catálogo (PIM Fase 21).
 *
 * **El archivo lo parsea el front**, igual que el importador de sellers: al backend le van
 * las filas ya en JSON. Es lo que evita subir un binario y volver a parsearlo del otro lado,
 * y es el contrato que la Fase 20 ya declaraba.
 *
 * **Ninguna corrida publica nada, y la pantalla lo dice antes de arrancar.** Los productos
 * nuevos quedan en Borrador y las actualizaciones sobre publicados quedan como versión
 * pendiente: sin ese aviso, el operador importa 300 filas y cree que están en el storefront.
 * Ese es el *handoff* — lo que la corrida genera lo tiene que aprobar un Admin de catálogo.
 *
 * **El polling se apaga cuando la corrida termina.** Un `setInterval` que sigue latiendo
 * contra una corrida `done` es el bug clásico de esta pantalla.
 */
export const CatalogImportPage: React.FC = () => {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const selectorDeArchivo = useRef<HTMLInputElement>(null);

  const [filas, setFilas] = useState<Array<Record<string, unknown>>>([]);
  const [columnas, setColumnas] = useState<string[]>([]);
  const [nombreArchivo, setNombreArchivo] = useState<string>("");
  const [mapping, setMapping] = useState<Record<string, string>>({});
  /**
   * En qué árbol se buscan las categorías (R5). Vacío es el de XCONS, que comparten la
   * mayoría de los stores; una tienda con árbol propio se elige acá.
   */
  const [storeCode, setStoreCode] = useState<string | undefined>();
  const { data: identity } = useGetIdentity<any>();
  const stores = useMemo(() => storesConCategorias(identity), [identity]);

  const [corrida, setCorrida] = useState<ImportRun | null>(null);
  const [reporte, setReporte] = useState<ImportReport | null>(null);
  const [arrancando, setArrancando] = useState(false);
  /**
   * El alto del cuerpo de la tabla del resumen: el lugar que le queda en el popup, menos su
   * encabezado. Se mide, porque lo de arriba (el ícono, los números) cambia de alto.
   */
  const [medirTabla, altoTabla] = useAltoDisponible(240);
  /** El resumen de una corrida es un popup: se abre al importar o al elegir una de la lista. */
  const [resumenAbierto, setResumenAbierto] = useState(false);
  const [historial, setHistorial] = useState<ImportRun[]>([]);
  /**
   * Las columnas que el API reconoce, con sus otros nombres (xcons-docs#468): es la misma
   * definición que escribe el export, así que lo exportado y las planillas que ya se usan
   * se mapean solas.
   */
  const [columnasConocidas, setColumnasConocidas] = useState<
    Array<{ code: string; label: string; aliases: string[] }>
  >([]);
  useEffect(() => {
    axiosInstance
      .get<Array<{ code: string; label: string; aliases: string[] }>>(
        `${API_URL_PIM_IMPORTS}/columns`,
      )
      .then(({ data }) => setColumnasConocidas(data ?? []))
      // Con un API anterior se cae al auto-mapeo por código, como antes.
      .catch(() => setColumnasConocidas([]));
  }, []);
  const [atributos, setAtributos] = useState<
    Array<{ code: string; label: string | null }>
  >([]);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * **Scroll infinito con lo que el API ya acepta: `limit`.** Llegar al final de la tabla
   * pide la lista otra vez con 20 más; si vuelven menos de las pedidas, no hay más.
   */
  const limiteDelHistorial = useRef(DE_A_CUANTAS);
  const [hayMasHistorial, setHayMasHistorial] = useState(false);
  const pidiendoMas = useRef(false);
  const cargarHistorial = useCallback(
    async (limit = limiteDelHistorial.current) => {
      try {
        const { data } = await axiosInstance.get<ImportRun[]>(
          API_URL_PIM_IMPORTS,
          { params: { limit } },
        );
        limiteDelHistorial.current = limit;
        setHistorial(data);
        setHayMasHistorial(data.length >= limit);
      } catch {
        setHistorial([]);
        setHayMasHistorial(false);
      }
    },
    [],
  );

  const alScrollearHistorial = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.target as HTMLElement;
    if (!el.classList?.contains("ant-table-body")) return;
    const cerca = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    if (!cerca || !hayMasHistorial || pidiendoMas.current) return;
    pidiendoMas.current = true;
    void cargarHistorial(limiteDelHistorial.current + DE_A_CUANTAS).finally(
      () => {
        pidiendoMas.current = false;
      },
    );
  };

  useEffect(() => {
    void cargarHistorial();
  }, [cargarHistorial]);

  /**
   * **A qué se puede mapear: las reservadas más el EAV entero.**
   *
   * Estaba fijo en diez campos, y con eso el importador no podía llegar a ningún lado: de
   * los ocho obligatorios de CON-P001, siete no estaban en la lista —`short_description`,
   * `unit_of_sale`, `inner`, `weight`, `vv_material`, `vv_use`, `vv_segment`—, así que toda
   * corrida terminaba en borradores que nadie podía mandar a revisión. El backend siempre
   * mapeó contra todo el EAV (es lo que el design pide, "mapeo dinámico y no un set fijo
   * como el import de sellers"); el que lo recortaba era este select.
   */
  useEffect(() => {
    axiosInstance
      .get<Array<{ code: string; label: string | null }>>(
        API_URL_PIM_ATTRIBUTES,
      )
      .then(({ data }) => setAtributos(data ?? []))
      // Sin atributos se puede mapear igual lo reservado: la pantalla no se cae por esto.
      .catch(() => setAtributos([]));
  }, []);

  const campos = useMemo(
    () => [
      ...RESERVADAS,
      ...atributos
        .map((a) => ({
          value: a.code,
          label: a.label ? `${a.label} (${a.code})` : a.code,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, "es")),
    ],
    [atributos],
  );

  /** Corta el polling. Se llama al terminar la corrida y al desmontar. */
  const detenerPolling = useCallback(() => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => detenerPolling, [detenerPolling]);

  /**
   * **El resumen se muestra entero, no de a pedazos.** Cuando la corrida termina se pide el
   * reporte antes de pintar nada, y la corrida y su tabla entran juntas: si no, el popup
   * mostraba el ícono, después la tabla, y crecía en cada paso. La primera consulta sale en
   * el momento, sin esperar el primer intervalo.
   */
  const seguir = useCallback(
    (runId: number) => {
      detenerPolling();
      let consultando = false;
      const consultar = async () => {
        if (consultando) return;
        consultando = true;
        try {
          const { data } = await axiosInstance.get<ImportRun>(
            `${API_URL_PIM_IMPORTS}/${runId}`,
          );
          if (data.status === "running") {
            setCorrida(data);
            return;
          }
          detenerPolling();
          const { data: rep } = await axiosInstance.get<ImportReport>(
            `${API_URL_PIM_IMPORTS}/${runId}/report`,
          );
          setCorrida(data);
          setReporte(rep);
          // El popup se abre recién acá, con todo adentro: mientras corre, lo dice el botón.
          setResumenAbierto(true);
          void cargarHistorial();
        } catch {
          detenerPolling();
        } finally {
          consultando = false;
        }
      };
      timer.current = setInterval(() => void consultar(), 1500);
      void consultar();
    },
    [detenerPolling, cargarHistorial],
  );

  const leerArchivo = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target?.result, { type: "array" });
        const hoja = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(hoja, {
          defval: "",
        });
        if (!json.length) {
          message.error("El archivo no tiene filas");
          return;
        }
        const cols = Object.keys(json[0]);
        setFilas(json);
        setColumnas(cols);
        setNombreArchivo(file.name);
        // Auto-mapeo por nombre exacto: es lo más común y ahorra el paso entero. Lo que no
        // matchea queda sin mapear y el operador lo elige.
        const auto: Record<string, string> = {};
        for (const col of cols) {
          const campo = campos.find(
            (c) => c.value.toLowerCase() === col.trim().toLowerCase(),
          );
          if (campo) {
            auto[col] = campo.value;
            continue;
          }
          // Por el nombre del form, el del export o el de la planilla de Magento: "Marca",
          // "Peso* (Kg)", "IVA*". Sólo si el campo existe entre los que se pueden elegir.
          const buscado = normalizarEncabezado(col);
          const conocida = columnasConocidas.find((c) =>
            [c.label, ...c.aliases].some(
              (a) => normalizarEncabezado(a) === buscado,
            ),
          );
          if (conocida && campos.some((c) => c.value === conocida.code)) {
            auto[col] = conocida.code;
          }
        }
        setMapping(auto);
        setCorrida(null);
        setReporte(null);
      } catch {
        message.error("No se pudo leer el archivo");
      }
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  const arrancar = async () => {
    // El SKU es la clave del upsert: sin él no hay nada que importar, y avisarlo acá es
    // mejor que un 422 con 300 filas ya mandadas. Puede venir armado o como Marca + SKU
    // Fabricante, que es cómo lo arma el form.
    const mapeados = Object.values(mapping);
    const conSkuArmado = mapeados.includes("sku");
    const conFabricante =
      mapeados.includes("sku_fabricante") && mapeados.includes("manufacturer");
    if (!conSkuArmado && !conFabricante) {
      message.error(
        "Hay que mapear el SKU, o la Marca y el SKU Fabricante para armarlo",
      );
      return;
    }
    setArrancando(true);
    try {
      // **El mapping viaja columna → campo, y adentro de `columns`.** Antes se invertía
      // a campo → columna y se mandaba plano: el backend lee `mapping.columns` y le
      // llegaba `undefined`, así que la corrida moría en la primera fila y quedaba en
      // `running` para siempre. El estado local ya tiene la dirección correcta — sólo hay
      // que sacar las columnas sin mapear.
      const columns = Object.fromEntries(
        Object.entries(mapping).filter(([, campo]) => campo),
      );
      const { data } = await axiosInstance.post<ImportRun>(
        API_URL_PIM_IMPORTS,
        {
          filename: nombreArchivo,
          mapping: { columns, ...(storeCode ? { storeCode } : {}) },
          rows: filas,
        },
      );
      setCorrida(data);
      setReporte(null);
      seguir(data.id);
    } catch (e) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudo arrancar la importación",
      );
    } finally {
      setArrancando(false);
    }
  };

  const descargarReporte = () => {
    if (!reporte) return;
    const hoja = XLSX.utils.json_to_sheet(
      reporte.rows.map((r) => ({
        Fila: r.lineNumber,
        SKU: r.sku ?? "",
        Resultado: ETIQUETA_FILA[r.status]?.label ?? r.status,
        "Producto Magento": r.magentoProductId ?? "",
        "Borrador o versión": r.pendingId ?? "",
        Código: r.errorCode ?? "",
        Error: r.errorMessage ?? "",
      })),
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, hoja, "Reporte");
    XLSX.writeFile(wb, `importacion-${reporte.run?.id ?? "reporte"}.xlsx`);
  };

  const corriendo = corrida?.status === "running";
  const procesadas = corrida
    ? corrida.drafts +
      corrida.pending +
      corrida.direct +
      corrida.failed +
      (corrida.unchanged ?? 0)
    : 0;

  /**
   * Cerrar el resumen vuelve a la carga, lista para otra planilla. La corrida queda elegida
   * para que su historial se siga viendo abajo, y si todavía corre el polling sigue.
   */
  const cerrarResumen = () => {
    setResumenAbierto(false);
    setFilas([]);
    setColumnas([]);
    setNombreArchivo("");
    setMapping({});
  };

  /** Lo que lleva el botón de la zona de carga: el mismo de Imágenes y videos de la ficha. */
  const botonDeCarga: React.CSSProperties = {
    background: token.colorPrimaryBg,
    color: token.colorPrimary,
    borderColor: token.colorPrimaryBorder,
    fontWeight: 600,
    paddingInline: 12,
  };

  const encabezado = (titulo: React.ReactNode) => ({
    style: TARJETA_CON_ENCABEZADO.style,
    // Poco aire entre la tarjeta y la tabla de adentro, que lleva su propio borde.
    styles: { header: TARJETA_CON_ENCABEZADO.header, body: { padding: 12 } },
    title: <Text style={TARJETA_CON_ENCABEZADO.titulo}>{titulo}</Text>,
  });

  return (
    <Flex vertical gap={16}>
      {/* Las tablas de alto fijo de la pantalla: la de columnas y Últimas importaciones. */}
      <style>{CSS_ALTO_FIJO}</style>
      {/* La cabecera de página del Admin, afuera de la tarjeta: ← de 32×32 a 16px del título. */}
      <Flex align="center">
        <ListButton
          hideText
          icon={<LeftOutlined />}
          onClick={() => navigate("/catalog")}
          style={{ marginRight: 16, flexShrink: 0 }}
        />
        <Title level={4} style={{ margin: 0 }}>
          Importar productos
        </Title>
      </Flex>

      {/* La carga, en secciones como las de la ficha. El resumen de lo importado es un popup. */}
      <ConfigProvider theme={TEMA_SECCION}>
        <Collapse
          // Siempre abiertas y sin flecha: acá no hay nada que plegar.
          activeKey={["archivo", "columnas"]}
          items={[
            {
              key: "archivo",
              showArrow: false,
              collapsible: "icon",
              label: <Text strong>Archivo</Text>,
              children: (
                <Upload.Dragger
                  accept=".xlsx,.xls,.csv"
                  maxCount={1}
                  showUploadList={false}
                  openFileDialogOnClick={false}
                  beforeUpload={leerArchivo}
                  disabled={corriendo}
                  style={{ cursor: "default" }}
                >
                  <Text
                    type="secondary"
                    style={{ display: "block", padding: "20px 16px 4px" }}
                  >
                    <Button
                      size="small"
                      style={botonDeCarga}
                      disabled={corriendo}
                      onClick={() => selectorDeArchivo.current?.click()}
                    >
                      Elegir archivo
                    </Button>{" "}
                    desde tu computadora, o arrastralo acá
                  </Text>
                  <Text
                    type="secondary"
                    style={{
                      display: "block",
                      fontSize: 12,
                      paddingBottom: 16,
                    }}
                  >
                    XLSX, XLS o CSV
                  </Text>
                  <input
                    ref={selectorDeArchivo}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) leerArchivo(file);
                      // Vacío, para que elegir el mismo archivo otra vez vuelva a dispararlo.
                      e.target.value = "";
                    }}
                  />
                </Upload.Dragger>
              ),
            },
            ...(columnas.length
              ? [
                  {
                    key: "columnas",
                    showArrow: false,
                    collapsible: "icon" as const,
                    label: (
                      <Text strong>
                        {nombreArchivo}{" "}
                        <Text type="secondary" style={{ fontWeight: 400 }}>
                          · {filas.length.toLocaleString("es-AR")} filas ·{" "}
                          {columnas.length} columnas
                        </Text>
                      </Text>
                    ),
                    children: (
                      <Flex vertical gap={16}>
                        {/* Del mismo alto que la tabla de Últimas importaciones: una
                            planilla con muchas columnas scrollea adentro, y el árbol y el
                            botón de importar quedan a la vista, abajo. */}
                        <div
                          className="tabla-alto-fijo"
                          style={{ height: ALTO_TABLA_FIJA }}
                        >
                          <Table
                            size="small"
                            rowKey="columna"
                            bordered
                            pagination={false}
                            scroll={{ y: "100%" }}
                            dataSource={columnas.map((c) => ({ columna: c }))}
                            columns={[
                              {
                                title: "Columna del archivo",
                                dataIndex: "columna",
                                key: "columna",
                                width: 240,
                              },
                              {
                                title: "Campo del producto",
                                key: "campo",
                                render: (
                                  _: unknown,
                                  row: { columna: string },
                                ) => (
                                  <Select
                                    allowClear
                                    style={{ minWidth: 260 }}
                                    placeholder="Sin mapear (se ignora)"
                                    value={mapping[row.columna]}
                                    onChange={(campo) =>
                                      setMapping((prev) => ({
                                        ...prev,
                                        [row.columna]: campo,
                                      }))
                                    }
                                    options={campos}
                                    showSearch
                                    optionFilterProp="label"
                                  />
                                ),
                              },
                              {
                                title: "Primera fila",
                                key: "muestra",
                                render: (
                                  _: unknown,
                                  row: { columna: string },
                                ) => (
                                  <Text type="secondary">
                                    {String(filas[0]?.[row.columna] ?? "")}
                                  </Text>
                                ),
                              },
                            ]}
                          />
                        </div>

                        {Object.values(mapping).includes("categories") && (
                          <Flex align="center" gap={8} wrap>
                            <Text>Árbol de categorías:</Text>
                            <Select
                              allowClear
                              style={{ minWidth: 260 }}
                              placeholder="XCONS (el árbol compartido)"
                              value={storeCode}
                              onChange={setStoreCode}
                              options={stores
                                .filter((code) => code !== "vv")
                                .map((code) => ({ value: code, label: code }))}
                              showSearch
                            />
                          </Flex>
                        )}

                        <Flex justify="flex-end">
                          <Button
                            type="primary"
                            icon={<UploadOutlined />}
                            iconPosition="end"
                            loading={arrancando || corriendo}
                            onClick={() => void arrancar()}
                          >
                            {corriendo
                              ? "Importando…"
                              : `Importar ${filas.length.toLocaleString(
                                  "es-AR",
                                )} filas`}
                          </Button>
                        </Flex>
                      </Flex>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </ConfigProvider>

      {corrida && (
        <ConfigProvider theme={TEMA_RESUMEN}>
          <Modal
            open={resumenAbierto}
            onCancel={cerrarResumen}
            width={600}
            // 700px de alto fijo; en una pantalla más baja, lo que entre. Lo que sobra lo
            // absorbe la tabla, que tiene su propio scroll.
            styles={{
              content: {
                height: "min(700px, calc(100vh - 32px))",
                display: "flex",
                flexDirection: "column",
              },
              body: { flex: 1, minHeight: 0, overflow: "hidden" },
            }}
            centered
            title={
              <Space>
                Importación #{corrida.id}
                {corriendo ? (
                  <Tag color="processing">En curso</Tag>
                ) : (
                  // Sin "Terminada": ya lo dice el ícono grande del resumen.
                  corrida.status === "failed" && (
                    <Tag color="red">Terminó con error</Tag>
                  )
                )}
              </Space>
            }
            // Sin botón de Cerrar: se cierra con la cruz.
            footer={
              reporte && (
                <Button icon={<DownloadOutlined />} onClick={descargarReporte}>
                  Descargar reporte
                </Button>
              )
            }
          >
            <Flex vertical gap={16} style={{ height: "100%" }}>
              {corriendo ? (
                <Progress
                  percent={
                    corrida.total
                      ? Math.round((procesadas / corrida.total) * 100)
                      : 0
                  }
                  status="active"
                />
              ) : (
                <Result
                  style={{ padding: "8px 0 0" }}
                  status={corrida.status === "failed" ? "warning" : "success"}
                  title={
                    corrida.status === "failed"
                      ? "La importación terminó con error"
                      : "Importación generada con éxito"
                  }
                  subTitle={
                    corrida.status === "failed"
                      ? // La corrida se marca `failed` cuando revienta después de procesar
                        // las filas: el reporte por fila sirve igual, y decirlo importa.
                        `${nombreDeCorrida(
                          corrida,
                        )} · el reporte por fila está completo`
                      : `${nombreDeCorrida(
                          corrida,
                        )} · ${corrida.total.toLocaleString("es-AR")} filas`
                  }
                />
              )}

              <Descriptions
                size="small"
                column={{ xs: 2, sm: 3, md: 6 }}
                items={[
                  { key: "total", label: "Filas", children: corrida.total },
                  {
                    key: "drafts",
                    label: "Borradores",
                    children: corrida.drafts,
                  },
                  {
                    key: "pending",
                    label: "Pendientes",
                    children: corrida.pending,
                  },
                  {
                    key: "direct",
                    label: "Directas",
                    children: corrida.direct,
                  },
                  {
                    key: "unchanged",
                    label: "Sin cambios",
                    children: corrida.unchanged ?? 0,
                  },
                  {
                    key: "failed",
                    label: "Con error",
                    children: (
                      <Text type={corrida.failed ? "danger" : undefined}>
                        {corrida.failed}
                      </Text>
                    ),
                  },
                ]}
              />

              {reporte && (
                <div ref={medirTabla} style={{ flex: 1, minHeight: 0 }}>
                  <Table
                    rowKey="id"
                    size="small"
                    bordered
                    dataSource={reporte.rows}
                    // Sin paginador: todas las filas, con scroll adentro del popup.
                    pagination={false}
                    // Lo único que scrollea es la tabla: su alto es lo que le deja el popup.
                    // Con el encabezado fijo antd usa anchos fijos: cada columna lleva el suyo,
                    // del tamaño de lo que muestra, y el Detalle se queda con el resto.
                    scroll={{ y: altoTabla }}
                    columns={[
                      {
                        title: "Fila",
                        dataIndex: "lineNumber",
                        key: "lineNumber",
                        width: 56,
                      },
                      {
                        title: "SKU",
                        dataIndex: "sku",
                        key: "sku",
                        width: 160,
                      },
                      {
                        title: "Resultado",
                        width: 140,
                        dataIndex: "status",
                        key: "status",
                        render: (s: string) => (
                          <Tag color={ETIQUETA_FILA[s]?.color}>
                            {ETIQUETA_FILA[s]?.label ?? s}
                          </Tag>
                        ),
                      },
                      {
                        title: "Detalle",
                        key: "detalle",
                        render: (_: unknown, r) =>
                          r.status === "failed" ? (
                            // Sólo el mensaje: el código (SKU_NEEDS_BRAND…) es para quien
                            // lo investiga, y viaja en el reporte que se descarga.
                            <Text type="secondary">
                              {r.errorMessage ?? r.errorCode}
                            </Text>
                          ) : r.pendingId ? (
                            // Las dos cosas viven en la misma tabla y comparten el id, pero
                            // no son lo mismo: un alta es un **borrador** y sólo un cambio
                            // sobre algo publicado es una **versión pendiente**. Decirle
                            // "versión pendiente" a un borrador manda a buscarlo a la cola de
                            // aprobación, donde no está.
                            <Text type="secondary">
                              {r.status === "draft"
                                ? "borrador"
                                : "versión pendiente"}{" "}
                              #{r.pendingId}
                            </Text>
                          ) : r.magentoProductId ? (
                            <Text type="secondary">
                              producto #{r.magentoProductId}
                            </Text>
                          ) : (
                            <Text type="secondary">—</Text>
                          ),
                      },
                    ]}
                  />
                </div>
              )}
            </Flex>
          </Modal>
        </ConfigProvider>
      )}

      <Card
        {...encabezado("Últimas importaciones")}
        style={{
          ...TARJETA_CON_ENCABEZADO.style,
          height: ALTO_ULTIMAS,
          display: "flex",
          flexDirection: "column",
        }}
        styles={{
          header: TARJETA_CON_ENCABEZADO.header,
          body: { padding: 12, flex: 1, minHeight: 0, overflow: "hidden" },
        }}
      >
        {historial.length ? (
          <div
            className="tabla-alto-fijo"
            onScrollCapture={alScrollearHistorial}
          >
            <Table
              rowKey="id"
              size="small"
              bordered
              dataSource={historial}
              pagination={false}
              scroll={{ y: "100%" }}
              onRow={(row) => ({
                style: { cursor: "pointer" },
                onClick: async () => {
                  // El reporte primero, y después el popup con todo junto.
                  if (row.status === "running") {
                    setCorrida(row);
                    setReporte(null);
                    setResumenAbierto(true);
                    seguir(row.id);
                    return;
                  }
                  const { data } = await axiosInstance.get<ImportReport>(
                    `${API_URL_PIM_IMPORTS}/${row.id}/report`,
                  );
                  setCorrida(row);
                  setReporte(data);
                  setResumenAbierto(true);
                },
              })}
              columns={[
                { title: "#", dataIndex: "id", key: "id", width: 70 },
                { title: "Archivo", dataIndex: "filename", key: "filename" },
                {
                  title: "Estado",
                  dataIndex: "status",
                  key: "status",
                  render: (s: string) => (
                    <Tag
                      color={
                        s === "running"
                          ? "processing"
                          : s === "failed"
                          ? "red"
                          : "green"
                      }
                    >
                      {s === "running"
                        ? "En curso"
                        : s === "failed"
                        ? "Con error"
                        : "Terminada"}
                    </Tag>
                  ),
                },
                {
                  title: "Filas",
                  dataIndex: "total",
                  key: "total",
                  align: "right",
                },
                {
                  title: "Pendientes",
                  dataIndex: "pending",
                  key: "pending",
                  align: "right",
                },
                {
                  title: "Con error",
                  dataIndex: "failed",
                  key: "failed",
                  align: "right",
                },
              ]}
            />
          </div>
        ) : (
          <Empty description="Todavía no se importó nada" />
        )}
      </Card>

      {/* El historial de la importación abierta (xcons-docs#436), siempre al final: sin una
          elegida se ve igual, vacío. Se vuelve a pedir cuando la corrida deja de estar en
          curso, que es cuando aparece el evento del cierre. */}
      <HistorialDelCatalogo
        module="import"
        entityId={corrida?.id}
        recarga={corriendo ? 0 : 1}
      />
    </Flex>
  );
};
